import { getCookie, setCookie } from '../utils/cookies';

const MOBILE_BREAKPOINT = 800;
const COOKIE_DAYS = 365;

function getBooleanAttr(element, name, defaultValue) {
	const value = element.getAttribute(name);
	if (value === null) return defaultValue;
	return value === 'true';
}

function getViewportMode() {
	return window.innerWidth >= MOBILE_BREAKPOINT ? 'desktop' : 'mobile';
}

function isCollapsible(widget, mode = getViewportMode()) {
	const attr = mode === 'desktop' ? 'data-collapsible-desktop' : 'data-collapsible-mobile';
	const defaultValue = mode === 'desktop' ? false : true;
	return getBooleanAttr(widget, attr, defaultValue);
}

function getDefaultCollapsed(widget, mode = getViewportMode()) {
	const attr = mode === 'desktop' ? 'data-collapsed-desktop-default' : 'data-collapsed-mobile-default';
	const defaultValue = mode === 'desktop' ? false : true;
	return getBooleanAttr(widget, attr, defaultValue);
}

function getPanelCookieName(widget) {
	const panelName = widget.getAttribute('data-panel-name');
	return panelName ? `ecko-${panelName}-panel-state` : '';
}

function getStoredCollapsed(widget) {
	const cookieName = getPanelCookieName(widget);
	if (!cookieName) return null;

	const state = getCookie(cookieName);
	if (state === 'open') return false;
	if (state === 'closed') return true;

	return null;
}

function saveCollapsed(widget, collapsed) {
	const cookieName = getPanelCookieName(widget);
	if (!cookieName) return;

	setCookie(cookieName, collapsed ? 'closed' : 'open', COOKIE_DAYS);
}

function setWidgetState(widget, collapsed) {
	const header = widget.querySelector('.sidebar-widget__header');
	const toggle = widget.querySelector('.sidebar-widget__toggle');
	const content = widget.querySelector('.sidebar-widget__content');
	const expanded = !collapsed;

	widget.setAttribute('data-collapsed', String(collapsed));

	if (toggle) {
		toggle.setAttribute('aria-expanded', String(expanded));
	}

	if (header) {
		header.setAttribute('aria-expanded', String(expanded));
	}

	if (content) {
		content.hidden = collapsed;
	}
}

function applyResponsiveState(widget, mode = getViewportMode()) {
	const collapsible = isCollapsible(widget, mode);
	widget.setAttribute('data-is-collapsible', String(collapsible));

	if (!collapsible) {
		setWidgetState(widget, false);
		return;
	}

	const storedCollapsed = getStoredCollapsed(widget);
	const collapsed = storedCollapsed ?? getDefaultCollapsed(widget, mode);
	setWidgetState(widget, collapsed);
}

export function initSidebarWidgets() {
	const widgets = Array.from(document.querySelectorAll('.sidebar-widget'));

	widgets.forEach(widget => {
		const header = widget.querySelector('.sidebar-widget__header');
		const toggle = widget.querySelector('.sidebar-widget__toggle');
		if (!header || !toggle) return;

		const controls = toggle.getAttribute('aria-controls');

		header.setAttribute('role', 'button');
		header.setAttribute('tabindex', '0');
		if (controls) {
			header.setAttribute('aria-controls', controls);
		}
		header.setAttribute('aria-label', toggle.querySelector('.sr-only')?.textContent || 'Toggle widget');

		applyResponsiveState(widget);
	});

	document.addEventListener('click', e => {
		const header = e.target.closest('.sidebar-widget__header');
		if (!header) return;

		const widget = header.closest('.sidebar-widget');
		if (!widget) return;

		const toggle = widget.querySelector('.sidebar-widget__toggle');
		const content = widget.querySelector('.sidebar-widget__content');

		if (!toggle || !content) return;
		if (!isCollapsible(widget)) return;

		const expanded = toggle.getAttribute('aria-expanded') === 'true';
		const collapsed = expanded;

		setWidgetState(widget, collapsed);
		saveCollapsed(widget, collapsed);
	});

	document.addEventListener('keydown', e => {
		if (e.key !== 'Enter' && e.key !== ' ') return;

		const header = e.target.closest('.sidebar-widget__header');
		if (!header) return;

		e.preventDefault();
		header.click();
	});

	let lastMode = getViewportMode();

	window.addEventListener('resize', () => {
		const mode = getViewportMode();
		if (mode === lastMode) return;

		lastMode = mode;
		widgets.forEach(widget => applyResponsiveState(widget, mode));
	}, { passive: true });
}
